export {
  classRecommendedMasteryPlan,
  defaultMasteryIntervalDays,
  defaultMasteryPlan,
  type MasteryPlan,
  masteryProgress,
  maximumMasteryIntervalDays,
  parseMasteryPlan,
} from "./mastery.js";

export const maximumSupplementaryLayouts = 30;

export type PracticeMethod =
  | { type: "consecutive"; target: number }
  | { type: "total_successes"; target: number }
  | { type: "success_in_attempts"; target: number; attempts: number }
  | { type: "fixed_attempts"; attempts: number }
  | { type: "fixed_time"; minutes: number };

export const methodNames = {
  consecutive: "連続成功",
  total_successes: "累計成功",
  success_in_attempts: "規定回数内の成功",
  fixed_attempts: "回数固定",
  fixed_time: "時間固定",
} as const;
export const methodDefaults: Record<PracticeMethod["type"], PracticeMethod> = {
  consecutive: { type: "consecutive", target: 3 },
  total_successes: { type: "total_successes", target: 10 },
  success_in_attempts: { type: "success_in_attempts", attempts: 10, target: 8 },
  fixed_attempts: { type: "fixed_attempts", attempts: 10 },
  fixed_time: { type: "fixed_time", minutes: 5 },
};
export function parsePracticeMethod(input: unknown): PracticeMethod {
  if (!input || typeof input !== "object" || Array.isArray(input))
    throw new Error("練習方法を選択してください。");
  const value = input as Record<string, unknown>;
  const count = (key: string, maximum = 1000) => {
    const n = value[key];
    if (typeof n !== "number" || !Number.isInteger(n) || n < 1 || n > maximum)
      throw new Error(
        `練習方法の数値は1〜${maximum}の整数で入力してください。`,
      );
    return n;
  };
  let result: PracticeMethod;
  switch (value.type) {
    case "consecutive":
    case "total_successes":
      result = { type: value.type, target: count("target") };
      break;
    case "success_in_attempts": {
      const attempts = count("attempts"),
        target = count("target");
      if (target > attempts)
        throw new Error("成功回数は挑戦回数以下にしてください。");
      result = { type: value.type, attempts, target };
      break;
    }
    case "fixed_attempts":
      result = { type: value.type, attempts: count("attempts") };
      break;
    case "fixed_time":
      result = { type: value.type, minutes: count("minutes", 300) };
      break;
    default:
      throw new Error("練習方法を選択してください。");
  }
  if (Object.keys(value).some((key) => !Object.keys(result).includes(key)))
    throw new Error("練習方法に未対応の項目が含まれています。");
  return result;
}
export function methodLabel(method: PracticeMethod) {
  switch (method.type) {
    case "consecutive":
      return `${method.target}回連続成功するまで`;
    case "total_successes":
      return `合計${method.target}回成功するまで`;
    case "success_in_attempts":
      return `${method.attempts}回中${method.target}回成功するまで`;
    case "fixed_attempts":
      return `${method.attempts}回取り組む`;
    case "fixed_time":
      return `${method.minutes}分間取り組む`;
  }
}
export const methodHelp: Record<PracticeMethod["type"], string> = {
  consecutive: "ミスすると連続成功は0に戻ります。",
  total_successes: "ミスしても、それまでの成功回数は残ります。",
  success_in_attempts:
    "規定回数を1セットとして判定します。未達成なら次のセットへ進みます。",
  fixed_attempts: "成否にかかわらず、指定した回数で終了します。",
  fixed_time: "指定した時間で終了し、成功数と成功率を記録します。",
};
export function scoreMethod(
  method: PracticeMethod,
  outcomes: readonly boolean[],
  seconds: number,
) {
  let successes = 0,
    streak = 0,
    blockSuccesses = 0,
    blockAttempts = 0,
    complete = false;
  for (const success of outcomes) {
    if (complete) throw new Error("練習終了後のショットが含まれています。");
    successes += Number(success);
    streak = success ? streak + 1 : 0;
    blockSuccesses += Number(success);
    blockAttempts += 1;
    switch (method.type) {
      case "consecutive":
        complete = streak === method.target;
        break;
      case "total_successes":
        complete = successes === method.target;
        break;
      case "success_in_attempts":
        if (blockAttempts === method.attempts) {
          complete = blockSuccesses >= method.target;
          if (!complete) {
            blockAttempts = 0;
            blockSuccesses = 0;
          }
        }
        break;
      case "fixed_attempts":
        complete = outcomes.length > 0 && blockAttempts === method.attempts;
        break;
      case "fixed_time":
        break;
    }
  }
  if (method.type === "fixed_time") complete = seconds >= method.minutes * 60;
  return {
    successes,
    streak,
    blockSuccesses,
    blockAttempts,
    complete,
    completedSets: Number(complete),
  };
}
export * from "./practice-groups.js";
